export type MapWallStyleId = 'minimal' | 'warm' | 'coastal' | 'atlas' | 'dark';
export type MapWallFormatId = 'a4' | 'a3' | 'a2' | '50x70' | '70x100' | 'square';
export type MapWallOrientation = 'portrait' | 'landscape';
export type MapWallMaterialId = 'poster' | 'framed' | 'acrylic' | 'aluminium' | 'tile';
export type MapWallMarkerId = 'pin' | 'heart' | 'home' | 'none';

export const mapWallStyles: Record<MapWallStyleId, {
  id: MapWallStyleId;
  name: string;
  description: string;
  stadiaStyle: string;
  background: string;
  panel: string;
  text: string;
  muted: string;
  border: string;
}> = {
  minimal: {
    id: 'minimal',
    name: 'Minimal black',
    description: 'Rustige zwart-wit kaart met sterke typografie.',
    stadiaStyle: 'alidade_smooth',
    background: '#f8fafc',
    panel: '#ffffff',
    text: '#0f172a',
    muted: '#64748b',
    border: '#111827',
  },
  warm: {
    id: 'warm',
    name: 'Warm neutral',
    description: 'Zachte premium tinten voor woonkamer en cadeau.',
    stadiaStyle: 'alidade_smooth',
    background: '#f5efe6',
    panel: '#fffaf2',
    text: '#2f251f',
    muted: '#8b6f5a',
    border: '#a18263',
  },
  coastal: {
    id: 'coastal',
    name: 'Coastal blue',
    description: 'Frisse kaartstijl voor eilanden, kustplaatsen en reizen.',
    stadiaStyle: 'outdoors',
    background: '#eef7fb',
    panel: '#f8fdff',
    text: '#0f2f3f',
    muted: '#4f7585',
    border: '#1f6f8b',
  },
  atlas: {
    id: 'atlas',
    name: 'Atlas',
    description: 'Klassieke cartografische uitstraling met rustige details.',
    stadiaStyle: 'outdoors',
    background: '#f6f0dc',
    panel: '#fff8e7',
    text: '#25311c',
    muted: '#6c7356',
    border: '#5c6b3c',
  },
  dark: {
    id: 'dark',
    name: 'Dark museum',
    description: 'Donkere statement-poster met subtiele kaartlijnen.',
    stadiaStyle: 'alidade_smooth_dark',
    background: '#080b10',
    panel: '#111827',
    text: '#f8fafc',
    muted: '#a8b2c1',
    border: '#d1d5db',
  },
};

export const mapWallFormats: Record<MapWallFormatId, {
  id: MapWallFormatId;
  label: string;
  widthMm: number;
  heightMm: number;
  productValue: string;
}> = {
  a4: { id: 'a4', label: 'A4', widthMm: 210, heightMm: 297, productValue: 'a4' },
  a3: { id: 'a3', label: 'A3', widthMm: 297, heightMm: 420, productValue: 'a3' },
  a2: { id: 'a2', label: 'A2', widthMm: 420, heightMm: 594, productValue: 'a2' },
  '50x70': { id: '50x70', label: '50 x 70 cm', widthMm: 500, heightMm: 700, productValue: '50x70' },
  '70x100': { id: '70x100', label: '70 x 100 cm', widthMm: 700, heightMm: 1000, productValue: '70x100' },
  square: { id: 'square', label: 'Vierkant 50 x 50 cm', widthMm: 500, heightMm: 500, productValue: 'square' },
};

export const mapWallMaterials: Record<MapWallMaterialId, {
  id: MapWallMaterialId;
  name: string;
  description: string;
  productValue: string;
}> = {
  poster: {
    id: 'poster',
    name: 'Premium poster',
    description: 'Mat posterpapier met luxe uitstraling.',
    productValue: 'poster',
  },
  framed: {
    id: 'framed',
    name: 'Poster met lijst',
    description: 'Zwart, wit of eiken lijst als complete wanddecoratie.',
    productValue: 'framed',
  },
  acrylic: {
    id: 'acrylic',
    name: 'Acrylglas',
    description: 'Glanzende premium print met dieptewerking.',
    productValue: 'acrylic',
  },
  aluminium: {
    id: 'aluminium',
    name: 'Aluminium',
    description: 'Strakke moderne print op stevig plaatmateriaal.',
    productValue: 'aluminium',
  },
  tile: {
    id: 'tile',
    name: 'Tegeltje',
    description: 'Klein persoonlijk aandenken voor plank of wand.',
    productValue: 'tile',
  },
};

export function formatCoordinates(lat: number, lng: number) {
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}° ${ns} / ${Math.abs(lng).toFixed(4)}° ${ew}`;
}

export function normalizeMapWallFormat(format?: string | null): MapWallFormatId {
  return Object.keys(mapWallFormats).includes(String(format)) ? String(format) as MapWallFormatId : 'a3';
}

export function normalizeMapWallStyle(style?: string | null): MapWallStyleId {
  return Object.keys(mapWallStyles).includes(String(style)) ? String(style) as MapWallStyleId : 'minimal';
}

export function normalizeMapWallOrientation(orientation?: string | null): MapWallOrientation {
  return orientation === 'landscape' ? 'landscape' : 'portrait';
}

export function normalizeMapWallMaterial(material?: string | null): MapWallMaterialId {
  return Object.keys(mapWallMaterials).includes(String(material)) ? String(material) as MapWallMaterialId : 'poster';
}

export function normalizeMapWallMarker(marker?: string | null): MapWallMarkerId {
  return marker === 'heart' || marker === 'home' || marker === 'none' ? marker : 'pin';
}
